import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { useToast } from '@/hooks/errors/use-toast';
import { PrivateImageGeneratorClient } from './api/client';

interface UseAddToProjectOptions {
  onSuccess?: (projectId: string | null) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for managing project assignment for private images
 * Handles updating workbook_id in workbook_images table
 * Now uses React Query for real-time project assignment status
 */
export const useAddToProject = (itemId: string, options: UseAddToProjectOptions = {}) => {
  const { toast } = useToast();
  const { user } = useUser();
  const queryClient = useQueryClient();
  
  // State for optimistic updates only
  const [optimisticProjectId, setOptimisticProjectId] = useState<string | null>(null);
  const [hasOptimisticUpdate, setHasOptimisticUpdate] = useState(false);

  // Query for current project assignment - automatically refetches when cache invalidated
  const projectAssignmentQuery = useQuery({
    queryKey: ['image-project-assignment', itemId],
    queryFn: async () => {
      if (!user?.id) return { projectId: null, isOwner: false };
      
      try {
        const [projectId, isOwner] = await Promise.all([
          PrivateImageGeneratorClient.getImageProject(itemId, user.id),
          PrivateImageGeneratorClient.checkImageOwnership(itemId, user.id)
        ]);
        
        return { projectId, isOwner };
      } catch (error) {
        console.warn('Failed to get project assignment:', error);
        return { projectId: null, isOwner: false };
      }
    },
    enabled: !!user?.id && !!itemId,
    staleTime: 1000 * 30, // 30 seconds - relatively fresh data
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid excessive API calls
  });

  // Get effective project ID (optimistic or actual)
  const currentProjectId = hasOptimisticUpdate ? optimisticProjectId : projectAssignmentQuery.data?.projectId || null;
  const isOwner = projectAssignmentQuery.data?.isOwner || false;

  // Mutation for updating image project assignment
  const updateMutation = useMutation({
    mutationFn: async (projectId: string | null) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      return await PrivateImageGeneratorClient.updateImageProject(itemId, user.id, projectId);
    },
    onMutate: async (projectId: string | null) => {
      // Optimistic update
      setOptimisticProjectId(projectId);
      setHasOptimisticUpdate(true);
    },
    onSuccess: (data, projectId) => {
      // Clear optimistic update
      setHasOptimisticUpdate(false);
      
      // Comprehensive cache invalidation for private image project assignments
      // Invalidate private image generator caches
      queryClient.invalidateQueries({ queryKey: ['private-image-generator'] });
      queryClient.invalidateQueries({ queryKey: ['private-image-generator-count'] });
      
      // FIXED: Invalidate workbook/project data caches with exact: false for filter variations
      queryClient.invalidateQueries({ queryKey: ['workbook-projects'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['workbook-images'] });
      
      // CRITICAL: Invalidate ALL project image library caches
      // Since we don't track the "old" project ID here, we'll invalidate all private-image-library caches
      queryClient.invalidateQueries({ queryKey: ['private-image-library'] }); // All projects
      queryClient.invalidateQueries({ queryKey: ['private-image-library-count'] }); // All counts
      
      // Also invalidate the specific project cache if we know the project ID
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ['private-image-library', projectId] });
        queryClient.invalidateQueries({ queryKey: ['private-image-library-count', projectId] });
      }
      
      // Invalidate universal storage caches (since private images can appear there)
      queryClient.invalidateQueries({ queryKey: ['universal-storage-items'] });
      queryClient.invalidateQueries({ queryKey: ['universal-library-data'] });
      
      // CRITICAL: Invalidate the infinite user images sidebar cache
      queryClient.invalidateQueries({ queryKey: ['infinite-user-images'] });
      
      // CRITICAL: Invalidate this image's project assignment query to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['image-project-assignment', itemId] });
      
      // Success feedback
      toast({
        title: "Success",
        description: projectId ? "Image assigned to project" : "Image removed from project",
      });
      
      options.onSuccess?.(projectId);
    },
    onError: (error, projectId) => {
      // Revert optimistic update
      setOptimisticProjectId(null);
      setHasOptimisticUpdate(false);
      
      // Check if this is a permission error and show appropriate message
      const isPermissionError = error.message.includes('You can only modify') || 
                               error.message.includes('permission') ||
                               error.message.includes('access denied');
      
      // Error feedback with better messaging
      toast({
        title: isPermissionError ? "Permission Denied" : "Error",
        description: isPermissionError 
          ? error.message 
          : `Failed to ${projectId ? 'assign to' : 'remove from'} project: ${error.message}`,
        variant: "destructive",
      });
      
      options.onError?.(error);
    },
  });

  // Helper functions
  const updateImageProject = useCallback(async (projectId: string | null) => {
    if (updateMutation.isPending) return;
    await updateMutation.mutateAsync(projectId);
  }, [updateMutation]);

  const removeFromProject = useCallback(async () => {
    if (updateMutation.isPending) return;
    await updateMutation.mutateAsync(null);
  }, [updateMutation]);

  return {
    currentProjectId,
    updateImageProject,
    removeFromProject,
    isPending: updateMutation.isPending,
    isLoading: updateMutation.isPending || projectAssignmentQuery.isLoading,
    error: updateMutation.error || projectAssignmentQuery.error,
    isOwner,
    // Expose query state for debugging
    isProjectAssignmentLoading: projectAssignmentQuery.isLoading,
    projectAssignmentError: projectAssignmentQuery.error,
  };
}; 